<template>
    <div>
        <div v-if="loading" class="d-flex justify-content-center my-5">
            <div class="modern-spinner"></div>
        </div>

        <div v-else>
            <!-- Create / Edit Form Toggle -->
            <AllowPermission :permission="`CREATE_${permissionCategory}`">
                <div class="section-header mb-3">
                    <SectionTitle>Payheads</SectionTitle>
                    <button class="toggle-form-btn" @click="showForm = true">
                        <i class="fas fa-plus"></i>
                        Add Payhead
                    </button>
                </div>
            </AllowPermission>

            <!-- Create / Edit Form Modal -->
            <RightSideModal :show="showForm" :title="editMode ? 'Edit Payhead' : 'New Payhead'" @close="closeForm">
                <Form
                    :fields="createFields"
                    title="Payhead"
                    submitUrl="/payheads"
                    updateRootUrl="/payheads"
                    @success="handleSuccess"
                    @cancel="closeForm"
                    :editMode="editMode"
                    :editData="editData"
                />
            </RightSideModal>

            <!-- Empty State -->
            <div v-if="!dataItems.length && !showForm" class="empty-state">
                <i class="fas fa-tags"></i>
                <p>No payheads found</p>
            </div>

            <!-- Card Grid -->
            <div class="payhead-grid" v-if="dataItems.length">
                <div
                    v-for="(item, idx) in dataItems"
                    :key="item.id"
                    class="payhead-card"
                    :style="{ animationDelay: `${idx * 0.04}s` }"
                >
                    <div class="payhead-card-top">
                        <div class="payhead-icon-wrapper">
                            <i class="fas" :class="getPayheadIcon(item)"></i>
                        </div>
                        <div class="payhead-actions">
                            <AllowPermission :permission="`EDIT_${permissionCategory}`">
                                <button class="action-btn edit-btn" @click="enableEditing(item)" title="Edit">
                                    <i class="fas fa-pen"></i>
                                </button>
                            </AllowPermission>
                            <AllowPermission :permission="`DELETE_${permissionCategory}`">
                                <button class="action-btn delete-btn" @click="deleteItem(item.id)" title="Delete">
                                    <i class="fas fa-trash-alt"></i>
                                </button>
                            </AllowPermission>
                        </div>
                    </div>

                    <div class="payhead-card-body">
                        <h4 class="payhead-name">{{ item.name }}</h4>
                        <div class="payhead-tags">
                            <span class="tag tag-type" v-if="item.payroll_payhead_type">
                                <i class="fas fa-layer-group me-1"></i>{{ item.payroll_payhead_type.name }}
                            </span>
                            <span class="tag tag-expense" v-if="item.payroll_expense_type">
                                <i class="fas fa-receipt me-1"></i>{{ item.payroll_expense_type.name }}
                            </span>
                        </div>
                    </div>

                    <div class="payhead-card-footer">
                        <div class="payhead-meta" v-if="item.defaultAmount">
                            <span class="meta-label">Default Amt</span>
                            <span class="meta-value">{{ item.defaultAmount }}</span>
                        </div>
                        <div class="payhead-meta" v-if="item.defaultPercentage">
                            <span class="meta-label">Default %</span>
                            <span class="meta-value">{{ item.defaultPercentage }}%</span>
                        </div>
                        <div class="payhead-badges">
                            <span v-if="item.isBasicSalary" class="mini-badge basic">Basic</span>
                            <span v-if="item.isLeavePayhead" class="mini-badge leave">Leave</span>
                            <span v-if="item.isOvertimePayhead" class="mini-badge overtime">OT</span>
                            <span v-if="item.onlyAllowFlatRate" class="mini-badge flat">Flat Only</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Form from "../../components/crud/form.vue"
import RightSideModal from "../../components/layout/RightSideModal.vue"
import PageCard from "../../components/layout/PageCard.vue"
import SectionTitle from "../../components/UI/SectionTitle.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import { createField } from "../../utils/common"
import { http } from "@/utils/http"
import { PERMISSION_CODES } from "../../store/permission"

export default {
    data() {
        return {
            PERMISSION_CODES,
            payheadMasters: {},
            loading: true,
            dataItems: [],
            showForm: false,
            editMode: false,
            editData: null,
            createFields: [
                createField("Name*", "name", "text", true),
                createField("Form Title", "placeholderTitle", "text", false),
                createField("Default Flat Amount", "defaultAmount", "number", false),
                createField(
                    "Or Default Percentage (of basic salary)",
                    "defaultPercentage",
                    "number",
                    false
                ),
                createField("Only Apply Above Amount", "onlyApplyAboveAmount", "number", false),
            ],
        }
    },
    components: { Form, RightSideModal, PageCard, SectionTitle, AllowPermission },
    computed: {
        permissionCategory() {
            return "PAYHEADS"
        },
    },
    async mounted() {
        await this.fetchPayheadCreationMasters()
        this.createFields = this.createFields.concat([
            createField(
                "Payhead Type*",
                "payheadTypeId",
                "select",
                true,
                "",
                this.payheadMasters.payheadTypes.map((item) => ({
                    id: item.id,
                    title: item.name,
                }))
            ),
            createField(
                "Expense Type*",
                "expenseTypeId",
                "select",
                true,
                "",
                this.payheadMasters.expenseTypes.map((item) => ({
                    id: item.id,
                    title: item.name,
                }))
            ),
            createField("Only Allow Flat Rate", "onlyAllowFlatRate", "switch", false, false),
            createField("Is Basic Salary", "isBasicSalary", "switch", false, false),
            createField("Is Leave Payhead", "isLeavePayhead", "switch", false, false),
            createField("Is Overtime Payhead", "isOvertimePayhead", "switch", false, false),
            createField(
                "Disable Company and Employee Edits",
                "disableCompanyAndEmployeeEdits",
                "switch",
                false,
                false
            ),
        ])
        await this.fetchData()
        this.loading = false
    },
    methods: {
        closeForm() {
            this.showForm = false
            this.editMode = false
            this.editData = null
        },
        enableEditing(item) {
            this.editMode = true
            this.editData = { ...item }
            this.showForm = true
            window.scrollTo({ top: 0, behavior: "smooth" })
        },
        async handleSuccess(result) {
            if (this.editMode) {
                this.dataItems = this.dataItems.map((item) =>
                    item.id === result.id ? result : item
                )
            } else {
                this.dataItems.unshift(result)
            }
            this.closeForm()
        },
        async fetchData() {
            try {
                const res = await http.get("/payheads")
                this.dataItems = res.data.result
            } catch (err) {
                console.log("error in fetching payheads", err)
            }
        },
        async deleteItem(id) {
            if (!confirm("Do you really want to delete this payhead?")) return
            try {
                await http.delete(`/payheads/${id}`)
                this.$toast.success("Payhead deleted successfully!")
                this.dataItems = this.dataItems.filter((item) => item.id !== id)
            } catch (err) {
                console.log("error deleting payhead", err)
                this.$toast.error("Failed to delete payhead")
            }
        },
        async fetchPayheadCreationMasters() {
            try {
                const res = await http.get("/payheads/masters")
                this.payheadMasters = res.data.result
            } catch (err) {
                console.log("error in fetching payhead masters", err)
            }
        },
        getPayheadIcon(item) {
            if (item.isBasicSalary) return "fa-money-bill-wave"
            if (item.isLeavePayhead) return "fa-calendar-minus"
            if (item.isOvertimePayhead) return "fa-clock"
            if (item.payroll_expense_type && item.payroll_expense_type.name && item.payroll_expense_type.name.toLowerCase().includes("deduction")) return "fa-minus-circle"
            return "fa-receipt"
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
}
.toggle-form-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border: 1px solid $gray200;
    border-radius: $radiusSm;
    background: white;
    color: $primaryColor;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all $transitionFast;

    &:hover {
        background: $primaryLight;
        border-color: rgba(0, 94, 137, 0.3);
    }
}
.form-wrapper {
    animation: slideDown 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
@keyframes slideDown {
    from { opacity: 0; transform: translateY(-8px); }
    to { opacity: 1; transform: translateY(0); }
}
.empty-state {
    text-align: center;
    padding: 60px 20px;
    color: $gray400;

    i {
        font-size: 40px;
        margin-bottom: 12px;
        display: block;
    }
    p {
        font-size: 14px;
        margin: 0;
    }
}

/* Card Grid */
.payhead-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 16px;
}

/* ---- Tablet ---- */
@media (max-width: 768px) {
    .payhead-grid {
        grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
        gap: 12px;
    }
    .payhead-actions {
        opacity: 1 !important; /* Always visible on touch */
    }
}
/* ---- Mobile ---- */
@media (max-width: 480px) {
    .payhead-grid {
        grid-template-columns: 1fr;
        gap: 12px;
    }
    .payhead-card {
        padding: 16px;
    }
    .section-header {
        flex-direction: column;
        align-items: flex-start;
    }
    .toggle-form-btn {
        width: 100%;
        justify-content: center;
    }
}
.payhead-card {
    background: white;
    border: 1px solid $gray200;
    border-radius: $radiusMd;
    padding: 20px;
    transition: all $transitionBase;
    animation: cardFadeIn 0.35s cubic-bezier(0.4, 0, 0.2, 1) both;

    &:hover {
        box-shadow: $shadowMd;
        border-color: $gray300;
        transform: translateY(-2px);
    }
}
@keyframes cardFadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
}
.payhead-card-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 14px;
}
.payhead-icon-wrapper {
    width: 40px;
    height: 40px;
    border-radius: $radiusSm;
    background: $primaryLight;
    color: $primaryColor;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
}
.payhead-actions {
    display: flex;
    gap: 6px;
    opacity: 0;
    transition: opacity $transitionFast;

    .payhead-card:hover & {
        opacity: 1;
    }
}
.action-btn {
    width: 32px;
    height: 32px;
    border: 1px solid $gray200;
    border-radius: $radiusSm;
    background: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    color: $gray500;
    transition: all $transitionFast;

    &.edit-btn:hover {
        color: $primaryColor;
        border-color: rgba(0, 94, 137, 0.3);
        background: $primaryLight;
    }
    &.delete-btn:hover {
        color: $danger;
        border-color: rgba(239, 68, 68, 0.3);
        background: #fef2f2;
    }
}
.payhead-card-body {
    margin-bottom: 14px;
}
.payhead-name {
    font-size: 15px;
    font-weight: 600;
    color: $gray800;
    margin: 0 0 10px 0;
    line-height: 1.3;
}
.payhead-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.tag {
    font-size: 11px;
    font-weight: 500;
    padding: 3px 10px;
    border-radius: $radiusFull;
    display: inline-flex;
    align-items: center;
}
.tag-type {
    background: #eff6ff;
    color: #2563eb;
}
.tag-expense {
    background: #f0fdf4;
    color: #16a34a;
}
.payhead-card-footer {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    padding-top: 12px;
    border-top: 1px solid $gray100;
}
.payhead-meta {
    display: flex;
    flex-direction: column;
}
.meta-label {
    font-size: 10px;
    color: $gray400;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-weight: 500;
}
.meta-value {
    font-size: 14px;
    font-weight: 600;
    color: $gray800;
}
.payhead-badges {
    display: flex;
    gap: 4px;
    margin-left: auto;
}
.mini-badge {
    font-size: 10px;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: 4px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}
.mini-badge.basic { background: #fef3c7; color: #92400e; }
.mini-badge.leave { background: #e0e7ff; color: #3730a3; }
.mini-badge.overtime { background: #fce7f3; color: #9d174d; }
.mini-badge.flat { background: $gray100; color: $gray600; }

/* ---- Mobile footer layout ---- */
@media (max-width: 480px) {
    .payhead-card-footer {
        flex-direction: column;
        align-items: flex-start;
    }
    .payhead-badges {
        margin-left: 0;
        margin-top: 6px;
    }
}

.modern-spinner {
    width: 32px;
    height: 32px;
    border: 3px solid $gray200;
    border-top-color: $primaryColor;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
